// Storefront (Design §14). These are the routes an anonymous visitor may reach — the matching
// allowlist lives in `AbstractID.publicRoutes`, and the two must stay in step: a route here that
// is missing there is redirected to AbstractID before it renders.
import Home from './pageComponents/store/Home.svelte';
import FamilyCompare from './pageComponents/store/FamilyCompare.svelte';
import Product from './pageComponents/store/Product.svelte';
import CheckoutCanceled from './pageComponents/store/CheckoutCanceled.svelte';
// Neither checkout page is public: both call `requireAuth` endpoints about a purchase whose
// customer signed in back in the confirmation window (§14), so an arrival with no token has
// nothing to render and the inherited redirect is the correct guard.
import CheckoutStart from './pageComponents/store/CheckoutStart.svelte';
import CheckoutReturn from './pageComponents/store/CheckoutReturn.svelte';
import CheckoutPay from './pageComponents/store/CheckoutPay.svelte';

// Account (Design §9, §14). Authenticated to the last route: every endpoint behind these pages is
// `requireAuth` and resolves through the signed-in user, so none of them belongs in
// `AbstractID.publicRoutes` — an anonymous arrival has nothing to render and the inherited
// redirect is the correct guard.
import AccountPurchases from './pageComponents/account/Purchases.svelte';
import AccountSubscription from './pageComponents/account/Subscription.svelte';
import AccountInvoices from './pageComponents/account/Invoices.svelte';
import AccountBilling from './pageComponents/account/Billing.svelte';

import Settings from './pageComponents/Settings.svelte';
import Handle404 from './pageComponents/404.svelte';

export default {
	'/': Home,
	// The Catalogue page was folded into the store page; the path still resolves, because it is
	// what customers bookmarked and what older emails link to.
	'/Products': Home,
	'/Products/:familySlug': FamilyCompare,
	'/Product/:slug': Product,
	'/Checkout/Canceled': CheckoutCanceled,
	'/Checkout/Start': CheckoutStart,
	'/Checkout/Return': CheckoutReturn,
	// Where a provider that hosts no payment page of its own sends the customer instead (§6.2).
	// Not public, like the two above: every request it makes is `requireAuth`.
	'/Checkout/Pay': CheckoutPay,

	/*
	 * `/Account` **is** Purchases. The account landing page and the purchases page were answering
	 * one question between them — "what have I got from this store" — from two screens that each
	 * held half the answer and linked to the other, so a customer looking for a subscription found
	 * entitlements, and one looking for what they were entitled to found a list of orders.
	 *
	 * This is the canonical path, because it is the one the sidebar points at and the one every
	 * "go to your account" link in checkout already uses. The other two are bookmarks and older
	 * emails, kept rather than 404ed: a dead link there reads as the purchases having gone rather
	 * than the page having moved.
	 */
	'/Account': AccountPurchases,
	'/Account/Subscriptions': AccountPurchases,
	'/Account/Purchases': AccountPurchases,
	'/Account/Subscription/:uuid': AccountSubscription,
	'/Account/Invoices': AccountInvoices,
	'/Account/Billing': AccountBilling,

	// Settings, and everything administrative with it. `Settings.svelte` mounts a nested router
	// over `/Settings/*`, so the whole of `/Settings/Admin/*` is declared in
	// `pageComponents/settings/routes.js` rather than here — including the user list, which was a
	// top-level `/Users`.
	'/Settings': Settings,
	'/Settings/*': Settings,

	'*': Handle404
};
